import { useEffect, useId, useLayoutEffect, useMemo, useRef } from "react";
import ModernTemplate from "@/components/templates/ModernTemplate";
import ClassicTemplate from "@/components/templates/ClassicTemplate";
import MinimalTemplate from "@/components/templates/MinimalTemplate";
import CreativeTemplate from "@/components/templates/CreativeTemplate";
import ExecutiveTemplate from "@/components/templates/ExecutiveTemplate";
import ATSPlainTemplate from "@/components/templates/ATSPlainTemplate";
import ATSCompactTemplate from "@/components/templates/ATSCompactTemplate";
import TwoColumnTemplate from "@/components/templates/TwoColumnTemplate";
import PhotoHeaderTemplate from "@/components/templates/PhotoHeaderTemplate";
import { FONT_FAMILIES } from "@/lib/typographyConstants";
import { isFullBleedTemplate } from "./TemplatePicker";

const MAP = {
  modern: ModernTemplate,
  classic: ClassicTemplate,
  minimal: MinimalTemplate,
  creative: CreativeTemplate,
  executive: ExecutiveTemplate,
  "ats-plain": ATSPlainTemplate,
  "ats-compact": ATSCompactTemplate,
  "two-column": TwoColumnTemplate,
  "photo-header": PhotoHeaderTemplate,
};

// A4 = 210 x 297 mm at 96dpi ≈ 794 x 1123 px. US Letter = 816 x 1056 px.
export const PAGE_SIZES = {
  a4: { width: 794, height: 1123, jsFormat: "a4" },
  letter: { width: 816, height: 1056, jsFormat: "letter" },
};

// 1 cm ≈ 37.8 px at 96 dpi. Continuation pages get a 2 cm top margin so
// content doesn't butt up against the paper edge, and the current page has
// a 2 cm bottom margin so nothing overflows into the reserved footer band.
// Together these enforce the printable safe area demanded by the user
// (2 cm top + 2 cm bottom on every physical page).
const PAGE_TOP_MARGIN = 76;
const PAGE_BOTTOM_MARGIN = 76;

const AVOID_SECTION_BREAK = new Set([
  "modern", "classic", "minimal", "creative", "executive",
  "ats-plain", "ats-compact", "photo-header", "two-column",
]);

// Emits the specificity-boosted CSS rule that binds the Project Name
// element (`[data-resume-projectname]`) to the Job Title typography
// category. The enclosing `[data-resume-projects]` ancestor is required
// so this rule wins over the projects catBlock's descendant `*` selector.
// Kept as a top-level helper so the JSX <style> child stays a plain
// template literal — React 19's style hoisting mangles inline IIFEs.
function buildProjectNameRule({ family, size, weight, color, scopeId }) {
  const lines = [];
  if (family) lines.push(`font-family: ${family} !important;`);
  if (size != null) lines.push(`font-size: ${size}pt !important;`);
  if (weight != null) lines.push(`font-weight: ${weight} !important;`);
  if (color) lines.push(`color: ${color} !important;`);
  lines.push(`line-height: 1.8 !important;`);
  return `[data-scope-id="${scopeId}"] [data-resume-projects] [data-resume-projectname], [data-scope-id="${scopeId}"] [data-resume-projects] [data-resume-projectname] * { ${lines.join(" ")} }`;
}

export default function ResumePreview({
  resume,
  template,
  pageSize = "a4",
  density = "comfortable",
  fontScale = 1,
  fontFamily = "Inter",
  descriptionFontFamily = "Inter",
  lineSpacing = 1.0,
  h1FontWeight = 700,
  h2FontWeight = 700,
  bodyFontWeight = 400,
  h1Color = "#0a0a0a",
  h2Color = "#0a0a0a",
  jobTitleFontSize = 11,
  jobTitleFontWeight = 600,
  jobTitleColor = "#0a0a0a",
  jobTitleFontFamily = "inherit",
  companyFontSize = 11,
  companyFontWeight = 400,
  companyColor = "#525252",
  companyFontFamily = "inherit",
  resumeNameFontSize = 22,
  resumeNameFontFamily = "inherit",
  sectionHeaderFontSize = 11,
  sectionHeaderFontFamily = "inherit",
  descriptionFontSize = 10,
  descriptionFontWeight = 400,
  descriptionColor = "#404040",
  contactInfoFontSize = 10,
  contactInfoFontWeight = 400,
  contactInfoColor = "#0a0a0a",
  contactInfoFontFamily = "inherit",
  workDescriptionFontSize = 10,
  workDescriptionFontWeight = 400,
  workDescriptionColor = "#0a0a0a",
  workDescriptionFontFamily = "inherit",
  dateFontSize = 8,
  dateFontWeight = 400,
  dateColor = "#0a0a0a",
  dateFontFamily = "inherit",
  locationFontSize = 8,
  locationFontWeight = 400,
  locationColor = "#0a0a0a",
  locationFontFamily = "inherit",
  bodyTypography = {},
  showWatermark = false,
  onMovedSectionsChange,
  onOverflowChange,
}) {
  const Component = MAP[template] || ModernTemplate;
  const containerRef = useRef(null);
  const lastMovedRef = useRef([]);
  const { width: WIDTH, height: PAGE } = PAGE_SIZES[pageSize] || PAGE_SIZES.a4;

  // Unique scope id so injected style rules only touch this paper instance
  // (avoids leaking overrides into other paper elements in the DOM).
  const scopeId = useId().replace(/[:]/g, "");
  const headerStack = useMemo(
    () => FONT_FAMILIES.find((f) => f.family === fontFamily)?.stack ||
      FONT_FAMILIES[0].stack,
    [fontFamily]
  );
  // Body stack is deliberately independent of `descriptionFontFamily` so
  // that changing the "Professional Summary" font family only affects the
  // summary section (via its dedicated catBlock rule) — not every body
  // element in the résumé. Defaults to Inter, the universal typography
  // baseline, keeping preset behaviour identical for un-customised users.
  const bodyStack = useMemo(
    () => FONT_FAMILIES[0].stack,
    []
  );

  // Resolves a per-category font-family choice ("inherit" | family name) to
  // the actual CSS stack. `fallback` decides which global stack an
  // inherit-value falls back to (header for name/section-headers, body for
  // description + body categories).
  const resolveFamily = (choice, fallback) => {
    if (!choice || choice === "inherit") return fallback;
    return FONT_FAMILIES.find((f) => f.family === choice)?.stack || fallback;
  };

  // Emits a per-category CSS declaration block. Empty `family` strings are
  // filtered so an "inherit" value doesn't override the parent cascade.
  // Optional `lineHeight` is applied only when caller passes it — used by
  // per-category line-spacing rules (1.3 for descriptions, 1.8 for headers).
  const catBlock = (attr, { family, size, weight, color, lineHeight, includeFamily = true }) => {
    const lines = [];
    if (includeFamily && family) lines.push(`font-family: ${family} !important;`);
    if (size != null)  lines.push(`font-size: ${size}pt !important;`);
    if (weight != null) lines.push(`font-weight: ${weight} !important;`);
    if (color)          lines.push(`color: ${color} !important;`);
    if (lineHeight != null) lines.push(`line-height: ${lineHeight} !important;`);
    if (lines.length === 0) return "";
    return `[data-scope-id="${scopeId}"] [${attr}], [data-scope-id="${scopeId}"] [${attr}] * { ${lines.join(" ")} }`;
  };

  const notifyMoved = (arr) => {
    const prevStr = JSON.stringify(lastMovedRef.current);
    const nextStr = JSON.stringify(arr);
    if (prevStr === nextStr) return;
    lastMovedRef.current = arr;
    // Defer to next tick to avoid commit-phase setState loop
    setTimeout(() => onMovedSectionsChange?.(arr), 0);
  };

  const lastOverflowRef = useRef(false);
  const notifyOverflow = (v) => {
    if (lastOverflowRef.current === v) return;
    lastOverflowRef.current = v;
    setTimeout(() => onOverflowChange?.(v), 0);
  };

  // Sections that must NEVER be split across pages. Only Work Experience is
  // allowed to break at entry boundaries — everything else moves as a whole
  // block. Detection is title-based so we don't need to touch every template.
  const isExperienceTitle = (title) => {
    if (!title) return false;
    const t = title.trim().toLowerCase();
    return t === "experience" || t === "work experience" || t === "employment" || t.startsWith("experience");
  };
  const getSectionTitle = (s) => {
    // First direct-descendant text-bearing element (works for every template
    // since section titles are always the first child of <section>).
    const first = s.querySelector("[data-resume-h2]") || s.firstElementChild;
    return first?.innerText || first?.textContent || "";
  };

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // ---------- 1) Apply font-scale to every inline fontSize -----------
    // We remember the original px value in data-orig-fs so the transform
    // is idempotent no matter how many times React re-renders.
    el.querySelectorAll("*").forEach((node) => {
      if (!node.style) return;
      const attr = "data-orig-fs";
      let orig = node.getAttribute(attr);
      if (!orig) {
        const inline = node.style.fontSize;
        if (!inline) return;
        const m = String(inline).match(/^([\d.]+)px$/);
        if (!m) return;
        orig = m[1];
        node.setAttribute(attr, orig);
      }
      node.style.fontSize = `${parseFloat(orig) * fontScale}px`;
    });

    // ---------- 2) Clear previous spacers (section & item level) -------
    el.querySelectorAll("[data-pagebreak-spacer]").forEach((n) => {
      n.style.marginTop = "";
      n.removeAttribute("data-pagebreak-spacer");
    });

    if (!AVOID_SECTION_BREAK.has(template)) {
      notifyMoved([]);
      return;
    }

    // ---------- 3) Compute pagination iteratively across up to 3 pages ----
    // Rules per user spec (iter 30 + margin fix iter 36):
    //  • Only Work Experience allowed to break at *entry* boundaries.
    //  • Every other section (Skills, Education, Projects, Certifications,
    //    Languages, Summary, Awards, Interests, References…) moves as a
    //    single indivisible block — never splits.
    //  • Every physical page reserves a 2 cm top + 2 cm bottom margin.
    //    Content that would cross into the bottom band gets pushed onto the
    //    next page, starting at the 2 cm top margin — this prevents split
    //    work-experience descriptions from bleeding into the printable edge.
    //  • If content still spans past page 3 the caller is notified via
    //    onOverflowChange(true) so the UI can warn the user.
    //
    // Runs pass 1 (fit into page 1) synchronously in the first rAF, then a
    // pass 2 (fit into page 2 after pass-1 shifts have been applied) in the
    // next rAF. Pass 2 is ADDITIVE — it adds to any margin-top already set
    // by pass 1 rather than replacing it.
    const paginatePass = (pageIndex, movedAcc, additive) => {
      // Content on page N must end before this Y coordinate (2 cm bottom
      // margin), and anything that would cross it moves to page N+1 starting
      // at `nextPageStart` (2 cm top margin from that page's top edge).
      const safeAreaEnd = PAGE * pageIndex - PAGE_BOTTOM_MARGIN;
      const nextPageStart = PAGE * pageIndex + PAGE_TOP_MARGIN;
      const containerRect = el.getBoundingClientRect();
      const zoomFactor = el.offsetWidth > 0 ? containerRect.width / el.offsetWidth : 1;
      const containerTop = containerRect.top;
      const applyShift = (node, delta) => {
        const current = parseFloat(node.style.marginTop) || 0;
        node.style.marginTop = `${additive ? current + delta : delta}px`;
      };
      const sections = Array.from(el.querySelectorAll("section"));
      for (const s of sections) {
        const rect = s.getBoundingClientRect();
        const top = (rect.top - containerTop) / zoomFactor;
        const bottom = top + rect.height / zoomFactor;
        if (bottom <= safeAreaEnd) continue;      // fits within page's safe area
        if (top >= safeAreaEnd) continue;         // already positioned in/past the reserved bottom margin — leave alone to avoid re-pushing on subsequent pagination passes

        const title = getSectionTitle(s);
        const canSplit = isExperienceTitle(title);
        let handled = false;

        if (canSplit) {
          // Work Experience: allow entry-level break. Each entry is a
          // [data-page-item] element (company + title + dates + bullets).
          const items = Array.from(s.querySelectorAll("[data-page-item]"));
          for (const item of items) {
            const iRect = item.getBoundingClientRect();
            const iTop = (iRect.top - containerTop) / zoomFactor;
            const iBottom = iTop + iRect.height / zoomFactor;
            if (iBottom <= safeAreaEnd) continue;      // this entry fits within safe area
            if (iTop < safeAreaEnd) {
              // Entry straddles safe-area boundary — push this entry (and
              // every entry after it) so it starts at the next page's
              // 2 cm top margin, leaving the current page's 2 cm bottom
              // margin untouched.
              applyShift(item, nextPageStart - iTop);
              item.setAttribute("data-pagebreak-spacer", "true");
              const label = item.textContent?.split("\n")[0]?.trim().slice(0, 60) || "";
              if (label) movedAcc.push(label);
              handled = true;
              break;
            }
            handled = true;
            break;
          }
        }

        if (!handled && rect.height / zoomFactor <= PAGE) {
          // Non-Experience section (or Experience with no splittable items):
          // move the entire section to the next page. This prevents orphan
          // section headers and split lists/skills/education entries.
          applyShift(s, nextPageStart - top);
          s.setAttribute("data-pagebreak-spacer", "true");
          if (title) movedAcc.push(title.trim());
        }
      }
    };

    const id = requestAnimationFrame(() => {
      const moved = [];
      paginatePass(1, moved, false);
      const id2 = requestAnimationFrame(() => {
        paginatePass(2, moved, true);
        // ---------- 4) Margin-collapse correction pass ------------------
        // CSS collapses each shifted element's `margin-top` with the
        // preceding sibling's `margin-bottom`, so the applied displacement
        // lands ~12-16 px short of the target. Re-measure each spacer's
        // actual rendered top, snap it to the nearest continuation page's
        // 2 cm top margin, and top up marginTop by the observed shortfall.
        // This is idempotent — sub-pixel deltas are ignored so we don't
        // enter a re-layout loop.
        const containerRectC = el.getBoundingClientRect();
        const zoomFactorC = el.offsetWidth > 0 ? containerRectC.width / el.offsetWidth : 1;
        const containerTopC = containerRectC.top;
        const spacers = Array.from(el.querySelectorAll("[data-pagebreak-spacer]"));
        for (const node of spacers) {
          const rect = node.getBoundingClientRect();
          const currentTop = (rect.top - containerTopC) / zoomFactorC;
          if (currentTop < PAGE - 4) continue; // still on page 1 — shouldn't happen but guard
          // Pick the page whose 2 cm top-margin band this element already
          // sits inside (or nearest below). PageIndex = 1 means page 2, etc.
          const pageIndex = Math.max(1, Math.round((currentTop - PAGE_TOP_MARGIN) / PAGE));
          const targetTop = PAGE * pageIndex + PAGE_TOP_MARGIN;
          const shortfall = targetTop - currentTop;
          if (shortfall > 0.5) {
            const current = parseFloat(node.style.marginTop) || 0;
            node.style.marginTop = `${current + shortfall}px`;
          }
        }
        notifyMoved(moved);
        // Detect content that spills past page 3 (the paper's maxHeight
        // clips visually but the underlying section rects still measure
        // the real height, so we can warn the user before content is lost).
        const containerRect2 = el.getBoundingClientRect();
        const zoomFactor2 = el.offsetWidth > 0 ? containerRect2.width / el.offsetWidth : 1;
        const containerTop2 = containerRect2.top;
        let maxBottom = 0;
        const sections = Array.from(el.querySelectorAll("section"));
        for (const s of sections) {
          const r = s.getBoundingClientRect();
          const b = (r.top - containerTop2) / zoomFactor2 + r.height / zoomFactor2;
          if (b > maxBottom) maxBottom = b;
        }
        notifyOverflow(maxBottom > PAGE * 3 + 4);
      });
      return () => cancelAnimationFrame(id2);
    });
    return () => cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resume, template, pageSize, density, fontScale, lineSpacing, jobTitleFontSize, jobTitleFontWeight, companyFontSize, companyFontWeight, resumeNameFontSize, sectionHeaderFontSize, descriptionFontSize, descriptionFontWeight, contactInfoFontSize, workDescriptionFontSize, dateFontSize, locationFontSize, bodyTypography]);

  // Sync the print `@page` size + margin to whichever paper size the user
  // picked in Layout Controls. Without this, a Letter-sized preview would
  // print onto A4 (or vice-versa) and trailing content wraps to a phantom
  // second page. Kept as a separate effect so the pagination logic above
  // stays focused. Rule is emitted once and updated in place.
  //
  // Full-bleed templates (TwoColumn, PhotoHeader) use `@page { margin: 0 }`
  // so their dark sidebar / coloured header banner can extend to the
  // physical paper edge. In that mode a companion `padding-top: 2cm` on
  // continuation-page spacers is emitted so page 2 body text still gets a
  // proper 2 cm top text-margin (which would otherwise be 0 from @page).
  useEffect(() => {
    const id = "mrd-print-page-size";
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement("style");
      el.id = id;
      document.head.appendChild(el);
    }
    const size = pageSize === "letter" ? "letter portrait" : "A4 portrait";
    const fullBleed = isFullBleedTemplate(template);
    const pageMargin = fullBleed ? "0" : "2cm";
    const spacerTop = fullBleed
      ? "@media print { section[data-pagebreak-spacer] { padding-top: 2cm !important; } }"
      : "";
    el.textContent = `@media print { @page { size: ${size}; margin: ${pageMargin}; } } ${spacerTop}`;
  }, [pageSize, template]);

  // ------- Build the scoped CSS string ------------------------------------
  // Assembled here (rather than inline inside the JSX <style> child) so that
  // React 19's style-hoisting path doesn't mangle our template-literal
  // children when they contain nested IIFEs / catBlock helper calls. We
  // inject the finished string via `dangerouslySetInnerHTML` below — the
  // most reliable way to give React a fully-formed CSS payload.
  const scopedCss = `
    [data-scope-id="${scopeId}"],
    [data-scope-id="${scopeId}"] * {
      font-family: ${bodyStack} !important;
      line-height: ${lineSpacing} !important;
      font-weight: ${bodyFontWeight} !important;
    }
    ${catBlock("data-resume-contact", {
      family: resolveFamily(contactInfoFontFamily, bodyStack),
      size: contactInfoFontSize,
      weight: contactInfoFontWeight,
      color: contactInfoColor,
      lineHeight: lineSpacing,
    })}
    ${catBlock("data-resume-description", {
      family: resolveFamily(descriptionFontFamily, bodyStack),
      size: descriptionFontSize,
      weight: descriptionFontWeight,
      color: descriptionColor,
      lineHeight: lineSpacing,
    })}
    ${catBlock("data-resume-skills", {
      family: resolveFamily(bodyTypography?.skills?.family, bodyStack),
      size: bodyTypography?.skills?.size,
      weight: bodyTypography?.skills?.weight,
      color: bodyTypography?.skills?.color,
      lineHeight: lineSpacing,
    })}
    ${catBlock("data-resume-education", {
      family: resolveFamily(bodyTypography?.education?.family, bodyStack),
      size: bodyTypography?.education?.size,
      weight: bodyTypography?.education?.weight,
      color: bodyTypography?.education?.color,
      lineHeight: lineSpacing,
    })}
    ${catBlock("data-resume-projects", {
      family: resolveFamily(bodyTypography?.projects?.family, bodyStack),
      size: bodyTypography?.projects?.size,
      weight: bodyTypography?.projects?.weight,
      color: bodyTypography?.projects?.color,
      lineHeight: lineSpacing,
    })}
    ${buildProjectNameRule({
      family: resolveFamily(jobTitleFontFamily, headerStack),
      size: jobTitleFontSize,
      weight: jobTitleFontWeight,
      color: jobTitleColor,
      scopeId,
    })}
    ${catBlock("data-resume-certifications", {
      family: resolveFamily(bodyTypography?.certifications?.family, bodyStack),
      size: bodyTypography?.certifications?.size,
      weight: bodyTypography?.certifications?.weight,
      color: bodyTypography?.certifications?.color,
      lineHeight: lineSpacing,
    })}
    ${catBlock("data-resume-additional", {
      family: resolveFamily(bodyTypography?.additional?.family, bodyStack),
      size: bodyTypography?.additional?.size,
      weight: bodyTypography?.additional?.weight,
      color: bodyTypography?.additional?.color,
      lineHeight: lineSpacing,
    })}
    ${catBlock("data-resume-workdesc", {
      family: resolveFamily(workDescriptionFontFamily, bodyStack),
      size: workDescriptionFontSize,
      weight: workDescriptionFontWeight,
      color: workDescriptionColor,
      lineHeight: lineSpacing,
    })}
    ${catBlock("data-resume-date", {
      family: resolveFamily(dateFontFamily, bodyStack),
      size: dateFontSize,
      weight: dateFontWeight,
      color: dateColor,
      lineHeight: lineSpacing,
    })}
    ${catBlock("data-resume-location", {
      family: resolveFamily(locationFontFamily, bodyStack),
      size: locationFontSize,
      weight: locationFontWeight,
      color: locationColor,
      lineHeight: lineSpacing,
    })}
    ${catBlock("data-resume-jobtitle", {
      family: resolveFamily(jobTitleFontFamily, headerStack),
      size: jobTitleFontSize,
      weight: jobTitleFontWeight,
      color: jobTitleColor,
      lineHeight: 1.8,
    })}
    ${catBlock("data-resume-company", {
      family: resolveFamily(companyFontFamily, headerStack),
      size: companyFontSize,
      weight: companyFontWeight,
      color: companyColor,
      lineHeight: lineSpacing,
    })}
    ${catBlock("data-resume-h1", {
      family: resolveFamily(resumeNameFontFamily, headerStack),
      size: resumeNameFontSize,
      weight: h1FontWeight,
      color: h1Color,
    })}
    ${catBlock("data-resume-h2", {
      family: resolveFamily(sectionHeaderFontFamily, headerStack),
      size: sectionHeaderFontSize,
      weight: h2FontWeight,
      color: h2Color,
      lineHeight: 1.8,
    })}
  `;

  return (
    <div
      ref={containerRef}
      data-scope-id={scopeId}
      data-template={template}
      className={`paper pdf-export relative density-${density}`}
      style={{
        width: "100%",
        maxWidth: WIDTH,
        margin: "0 auto",
        minHeight: PAGE,
        maxHeight: PAGE * 3,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Scoped style block — see `scopedCss` above for details. Injected via
          dangerouslySetInnerHTML because React 19's <style> child pipeline
          otherwise mangles our template-literal payload down to "NaN". */}
      <style dangerouslySetInnerHTML={{ __html: scopedCss }} />
      <div style={{ width: "100%", flex: "1 1 auto", display: "flex", flexDirection: "column" }}>
        <Component resume={resume} />
      </div>

      {/* Watermark: a subtle "Created with [logo] MyResumeDesign" chip in the
          bottom-right corner of every physical page. Removed after Razorpay
          payment (paidClean session flag). Positions past the paper's actual
          rendered height are clipped by .paper's overflow:hidden. */}
      {showWatermark && (
        <div
          className="watermark-overlay"
          data-testid="resume-watermark"
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            overflow: "hidden",
            zIndex: 10,
          }}
        >
          {/* Diagonal anti-screenshot watermark — one subtle "MyResumeDesign.com"
              line rotated across the centre of each page. Low opacity keeps
              the résumé readable but any screenshot captures the mark. */}
          {[1, 2, 3].map((page) => (
            <div
              key={`watermark-diag-${page}`}
              data-testid={`watermark-diagonal-p${page}`}
              style={{
                position: "absolute",
                top: `${PAGE * (page - 1) + PAGE / 2}px`,
                left: "50%",
                transform: "translate(-50%, -50%) rotate(-28deg)",
                fontFamily: "'Space Grotesk', 'Inter', sans-serif",
                fontWeight: 800,
                fontSize: "44px",
                letterSpacing: "0.02em",
                color: "#0a0a0a",
                opacity: 0.08,
                whiteSpace: "nowrap",
                userSelect: "none",
              }}
            >
              MyResumeDesign.com
            </div>
          ))}

          {/* Bottom-right corner branding chip — kept in addition to the
              diagonal mark so paying users see clean output and free users
              have a visible attribution too. */}
          {[1, 2, 3].map((page) => (
            <div
              key={`watermark-page-${page}`}
              data-testid={`watermark-corner-p${page}`}
              style={{
                position: "absolute",
                top: `${PAGE * page - 34}px`,
                right: "20px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "4px 8px",
                background: "rgba(255,255,255,0.55)",
                border: "1px solid rgba(10,10,10,0.10)",
                borderRadius: "3px",
                fontFamily: "'Inter', system-ui, sans-serif",
                fontSize: "9px",
                fontWeight: 600,
                letterSpacing: "0.02em",
                color: "#0a0a0a",
                whiteSpace: "nowrap",
                userSelect: "none",
                opacity: 0.75,
                backdropFilter: "blur(2px)",
              }}
            >
              <span style={{ opacity: 0.7 }}>Created with</span>
              <svg width="10" height="10" viewBox="0 0 24 24" style={{ display: "block", flexShrink: 0 }} aria-hidden="true">
                <polygon points="0,0 24,0 24,18.72 18.72,24 0,24" fill="#2400FF" />
                <polygon points="24,18.72 18.72,24 24,24" fill="#ffffff" />
                <rect x="15.36" y="1.44" width="7.68" height="2.4" fill="#FF2A00" />
              </svg>
              <span style={{ fontWeight: 700 }}>MyResumeDesign</span>
            </div>
          ))}
        </div>
      )}
      {/* Dashed page-break rulers — one at PAGE, one at 2×PAGE. They live
          in the preview only (hidden in print via @media print). */}
      {[1, 2].map((n) => (
        <div
          key={`page-indicator-${n}`}
          aria-hidden
          data-page-indicator="true"
          data-html2canvas-ignore="true"
          style={{
            position: "absolute", left: 0, right: 0, top: PAGE * n,
            borderTop: "1px dashed rgba(255, 42, 0, 0.5)",
            pointerEvents: "none",
          }}
        >
          <span
            style={{
              position: "absolute", right: 8, top: -8,
              background: "#fff", color: "#FF2A00",
              padding: "0 6px",
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 9, letterSpacing: "0.2em", textTransform: "uppercase",
            }}
          >
            Page {n + 1} ↓
          </span>
        </div>
      ))}
    </div>
  );
}
